//
// File: PluginLoader.ts
// Description: A collection of functions for managing the loading of remote modules and containers
// TODO: This should be converted to a static class or singleton
// Copyright: Broadlume 2024
//

import { IShellPluginConfig } from './IShellPluginConfig';

// forward declaration of webpack functions
declare const __webpack_init_sharing__: (scope: 'default') => Promise<void>;
declare const __webpack_share_scopes__: { default: unknown };

/**
 * Cache for loaded modules, key is 'url:modulename'
 */
const kRemoteModuleCache = new Map<string, unknown>();

/**
 * Cache for loaded containers, key is 'url'
 */
const kRemoteContainerCache = new Map<string, unknown>();

/*
 * Is the src defined in the config loaded in the container cache
 * @param config - the shell plugin config to check
 */
export function isContainerLoaded(config: IShellPluginConfig): boolean {
  return kRemoteContainerCache.has(config.src);
}

/*
 * Is the module 'config.module' loaded from the container 'config.src'
 * @param config - the shell plugin config to check
 */
export function isModuleLoaded(config: IShellPluginConfig): boolean {
  return kRemoteModuleCache.has(`${config.src}:${config.module}`);
}

/*
 * Return the webpack module from the cache for the given config based
 * on 'config.src' and 'config.module'.
 * @param config - the shell plugin config to check
 * @returns the module if it exists in the cache, otherwise undefined
 */
export function getModule(config: IShellPluginConfig): unknown {
  return kRemoteModuleCache.get(`${config.src}:${config.module}`);
}

/**
 * Main function to load a remote module,
 * If required imports the container first before invoking and extracting
 * the default export as the module
 * Caches the module in a map for future use
 * @param config - the shell plugin config to load
 * @returns A webpack module from the remote container
 */
export async function loadRemoteModule(config: IShellPluginConfig) {
  // create key for the module
  const remoteModuleKey = `${config.src}:${config.module}`;
  // check if module is already loaded, if so return it
  if (kRemoteModuleCache.has(remoteModuleKey)) {
    return kRemoteModuleCache.get(remoteModuleKey);
  }

  // get container from cache or load it
  const container = kRemoteContainerCache.has(config.src)
    ? kRemoteContainerCache.get(config.src)
    : await loadRemoteContainer(config).catch((err) => {
        console.error(`Failed to load container for ${config.src}:`, err);
        throw err;
      });

  // load module from container and extract default export as Module
  const factory = await container.get(config.module);

  const Module = factory().default;

  // cache module
  kRemoteModuleCache.set(remoteModuleKey, Module);

  return Module;
}

/** Marks stylesheets owned by a federated plugin. */
const SHELL_PLUGIN_SRC_ATTR = 'data-shell-plugin-src';

function normalizePluginSrc(src: string): string {
  return src.replace(/\/$/, '');
}

/** Plugin origins from remoteEntry scripts and tagged stylesheet links. */
function getKnownPluginSrcs(): string[] {
  const srcs = new Set<string>();

  document.querySelectorAll('script[src*="remoteEntry.js"]').forEach((node) => {
    const base = (node as HTMLScriptElement).src.replace(/remoteEntry\.js(?:\?.*)?$/, '');
    if (base) {
      srcs.add(normalizePluginSrc(base));
    }
  });

  document.querySelectorAll(`link[${SHELL_PLUGIN_SRC_ATTR}]`).forEach((node) => {
    const tagged = (node as HTMLLinkElement).getAttribute(SHELL_PLUGIN_SRC_ATTR);
    if (tagged) {
      srcs.add(normalizePluginSrc(tagged));
    }
  });

  return Array.from(srcs);
}

/** Ownership: data attribute first, else href prefix vs known plugin origins. */
function resolveLinkPluginSrc(link: HTMLLinkElement, knownSrcs: string[]): string | null {
  const tagged = link.getAttribute(SHELL_PLUGIN_SRC_ATTR);
  if (tagged) {
    return normalizePluginSrc(tagged);
  }

  for (const src of knownSrcs) {
    if (link.href.startsWith(src + '/') || link.href.startsWith(src)) {
      return src;
    }
  }

  return null;
}

/**
 * Enable the active plugin's CSS; disable other plugin stylesheets.
 * Moves active links to end of <head> for cascade order.
 */
export function activateRemotePluginCss(config: IShellPluginConfig): void {
  if (typeof document === 'undefined' || !config?.src) {
    return;
  }

  const activeSrc = normalizePluginSrc(config.src);
  const knownSrcs = getKnownPluginSrcs();
  if (!knownSrcs.includes(activeSrc)) {
    knownSrcs.push(activeSrc);
  }

  const activeLinks: HTMLLinkElement[] = [];

  document.querySelectorAll('link[rel="stylesheet"]').forEach((node) => {
    const link = node as HTMLLinkElement;
    const owner = resolveLinkPluginSrc(link, knownSrcs);
    if (!owner) {
      return;
    }

    if (!link.getAttribute(SHELL_PLUGIN_SRC_ATTR)) {
      link.setAttribute(SHELL_PLUGIN_SRC_ATTR, owner);
    }

    if (owner === activeSrc) {
      link.disabled = false;
      activeLinks.push(link);
    } else {
      link.disabled = true;
    }
  });

  activeLinks.forEach((link) => {
    document.head.appendChild(link);
  });
}

/** Disable the departing plugin's stylesheets only (no re-enable of others). */
export function deactivateRemotePluginCss(config: IShellPluginConfig): void {
  if (typeof document === 'undefined' || !config?.src) {
    return;
  }

  const departingSrc = normalizePluginSrc(config.src);
  const knownSrcs = getKnownPluginSrcs();
  if (!knownSrcs.includes(departingSrc)) {
    knownSrcs.push(departingSrc);
  }

  document.querySelectorAll('link[rel="stylesheet"]').forEach((node) => {
    const link = node as HTMLLinkElement;
    const owner = resolveLinkPluginSrc(link, knownSrcs);
    if (owner !== departingSrc) {
      return;
    }
    if (!link.getAttribute(SHELL_PLUGIN_SRC_ATTR)) {
      link.setAttribute(SHELL_PLUGIN_SRC_ATTR, departingSrc);
    }
    link.disabled = true;
  });
}

function ensurePluginStylesheetLink(cssUrl: string, pluginSrc: string): HTMLLinkElement {
  let link = document.querySelector(`link[href="${cssUrl}"]`) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = cssUrl;
    document.head.appendChild(link);
  }
  link.setAttribute(SHELL_PLUGIN_SRC_ATTR, pluginSrc);
  return link;
}

/**
 * Load remote CSS, tag the link, and activate (disable other plugins' CSS).
 */
export async function loadRemoteCss(config: IShellPluginConfig) {
  activateRemotePluginCss(config);
  // create key for the module
  const remoteCssName = config.css || './Css';
  const remoteModuleKey = `${config.src}:${remoteCssName}`;
  // check if module is already loaded, if so return it
  if (kRemoteModuleCache.has(remoteModuleKey)) {
    const cached = kRemoteModuleCache.get(remoteModuleKey);
    if (typeof cached === 'string' && cached.endsWith('.css')) {
      ensurePluginStylesheetLink(cached, config.src);
    }
    return cached;
  }

  const container = kRemoteContainerCache.has(config.src)
    ? kRemoteContainerCache.get(config.src)
    : await loadRemoteContainer(config).catch((err) => {
        throw err;
      });

  // load module from container and extract default export as Module
  const factory = await container.get(remoteCssName);

  // The factory for a CSS module in Vite federation returns a promise
  // that resolves to an object with the URL of the CSS file.
  // We don't execute it as a module, but rather create a link tag.
  try {
    const cssModule = await factory();
    if (cssModule && cssModule.default) {
      const cssUrl = cssModule.default;
      if (typeof cssUrl === 'string' && cssUrl.endsWith('.css')) {
        ensurePluginStylesheetLink(cssUrl, config.src);
        kRemoteModuleCache.set(remoteModuleKey, cssUrl);
        return cssUrl;
      }
    }
  } catch (e) {
    // This might happen if the remote is not a Vite remote, or if the CSS is handled differently.
    // The factory() call itself might be what injects the CSS for some bundlers.
    const Module = factory();
    kRemoteModuleCache.set(remoteModuleKey, Module);
    return Module;
  }
}

// ensure webpack sharing is initialised once
let kWebpackSharingInitalised = false;

interface RemoteModule {
  get: (request: any) => any;
  init: (arg: any) => void;
}

const fetchRemoteModule = (url: string, remoteName: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    const moduleMap = {
      LeadsFrontend: 'text/javascript',
    } as Record<string, string>;
    const script = document.createElement('script');
    script.src = url;
    // Only load the Leads (Vite) frontend as an ES module; fall back to classic script for others
    script.type = moduleMap[remoteName] ?? 'text/javascript';
    script.async = true;

    script.onload = () => {
      // For Vite federation, the module registers itself globally
      // Try different possible global names
      const remote = (window as any)[remoteName] as RemoteModule;

      if (remote) {
        const proxy = {
          get: (request: any) => {
            return remote.get(request);
          },
          init: (arg: any) => {
            try {
              remote.init(arg);
            } catch (e) {
              reject(e);
            }
          },
        };
        resolve(proxy);
      } else {
        reject(new Error(`Remote ${remoteName} not found on window object`));
      }
    };

    script.onerror = (error) => {
      reject(new Error(`Failed to load remote script from ${url}`));
    };

    document.head.appendChild(script);
  });
};

/**
 * Load the remote container, if the config.src does not provide
 * a file name then remoteEntry.js will be appended to the url.
 * Invokes the webpack init function on the imported container
 * then caches it before returning it
 * @param config - the shell plugin config to load
 * @returns A webpack container from the remote url
 */
async function loadRemoteContainer(config: IShellPluginConfig) {
  if (!config) {
    throw new Error('Invalid config passed to loadRemoteContainer.');
  }

  if (!kWebpackSharingInitalised) {
    kWebpackSharingInitalised = true;
    await __webpack_init_sharing__('default');
  }

  // build container url but appending remoreEntry.js to url if required
  const containerUrl = `${config.src}${config.src.endsWith('/') ? '' : '/'}remoteEntry.js`;

  const container = await fetchRemoteModule(containerUrl, config.webpackName || 'frontend');
  await container.init(__webpack_share_scopes__.default);

  kRemoteContainerCache.set(config.src, container);
  return container;
}
